import 'whatwg-fetch';

// Libs
import React, { useState, useEffect } from 'react';
import { useSelector, Provider } from 'react-redux';
// Auth
import { Bridge } from '@nackle/bridge';
// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';

import { Layout, Spin } from '@nackle/origami';

import umbrella from '@nackle/umbrella';
const { loadModule } = umbrella;

import createAppRoutes, { getMiddleware } from './app-routes.jsx';
import createAppStore from './app-store';

// Components
import Fallback from './components/shared/fallback/fallback.jsx';
import ErrorBoundary from './components/shared/error-boundry/error-boundry.jsx';
import Content from './components/shared/content/content.jsx';
import SessionModal from './components/shared/session/session-modal.jsx';
import NotFound from './components/shared/not-found/not-found.jsx';
import { notification } from '@nackle/origami';
import { sendErrorEvent } from './utils/faro-event.js';
import { idUrl, deckUrl } from 'endpoints';
import { faro } from '@grafana/faro-web-sdk';
// Styles
import './styles/app.less';
// load deck
const AppRoot = await loadModule( deckUrl, 'deck', './AppRoot', 'component-deck' );
import experienceModules from '../experiences.js';
// load all the experience modules
let experiences = await Promise.all(
    experienceModules.map( ( { name, url, identity } ) => (
        loadModule( url, name, './experience', identity )
    ) )
);
// filter out any experiences that didn't load
experiences = experiences.filter( ( experience, index ) => {
    if ( !experience ) {
        return false;
    }
    return true;
} );
// create routes
const routes = createAppRoutes( experiences );
// create redux store
const store = createAppStore( experiences );

/*******************************************************************************
 *  1. App (root level component)
*/
export default function App( { auth } ) {
    routeHistory.applyMiddleware( getMiddleware( store, auth ) );
    routeHistory.applyRoutes( routes );
    const isSignedIn = auth.state.id_token;
    const [ user, setUser ] = useState( {} );
    const [ queryLocale, setQueryLocale ] = useState();
    const [ source, setSource ] = useState();
    const [ profileLocale, setProfileLocale ] = useState();
    const [ restored, setRestored ] = useState( false );
    const [ restoring, setRestoring ] = useState( false );

    const { DeckContext } = AppRoot;
    useEffect( () => {
        // check if anything passed in a locale in a query string
        if ( window.location.search ) {
            const params = new URLSearchParams( window.location.search );
            setQueryLocale( params.get( 'locale' ) );
            setSource( params.get( 'source' ) );
            //this is coming from the postRestoreLocation from identity assumption
            setRestored( params.get( 'restored' ) );
            //if we were just restored, remove the query string from the url
            if ( params.get( 'restored' ) === 'true' ) {
                setRestoring( true );
                routeHistory.replace( `${ window.location.origin }${ window.location.pathname }` );
                setTimeout( ()=> {
                    setRestoring( false );
                }, 5000 );

            }
            //restoring doesn't work from dmsa so it will send the user back to umbrella with
            //a query string of `?restore-identity=true`. This looks for that and restores
            //identity when present. Also prevents rendering while switching back.
            if ( params.get( 'restore-identity' ) === 'true' ) {
                auth.restoreIdentity();
            }

        }
    }, [ window.location ] );

    const handleAuthChange = async ( user ) => {
        setUser( user );
        faro?.api?.setUser( {
            id: user.claims?.person_id,
            email: user.claims?.email,
            username: user.claims?.username,
        } );
    };
    const displaySuccessMessage = async ( message, description, time ) => {
        notification.success( message, description, time );
    };

    const updateLocale = ( locale ) => {
        setProfileLocale( locale );
        setLocaleInDB( locale );
    };
    const onSessionExpiring =  () => {
        auth.renew();
    };

    return (

        <Provider store={ store }>
            <Bridge
                auth={ auth }
                onSignIn={ handleAuthChange }
                onAuthStateChange={ handleAuthChange }
                onSessionExpiring={ onSessionExpiring }
                restoring={ restoring }
                requiresAuth={ false } // handled in route render
            >
                <React.Suspense>
                    <AppRoot.default
                        auth={ auth }
                        user={ user }
                        guest={ !isSignedIn }
                        footer={ false }
                        locale={ profileLocale ? profileLocale : queryLocale ? queryLocale : undefined }
                        isUmbrella={ true }
                        idUrl={ idUrl }
                        restored={ restored }
                        restoring={ restoring }
                    >
                        {
                            <DeckContext.Consumer>
                                {
                                    value =>
                                        <div className="app-content">
                                            <ErrorBoundary>
                                                <React.Suspense fallback={ <Fallback loaderColor={ value?.branding?.formattedStyles?.primaryColor } /> }>
                                                    <Route render={ route => {

                                                        return (
                                                            <RouteWrapper
                                                                route={ route }
                                                                value={ value }
                                                                isSignedIn={ isSignedIn }
                                                                auth={ auth }
                                                                profileLocale={ profileLocale }
                                                                routes={ routes }
                                                                user={ user }
                                                                source={ source }
                                                                displaySuccessMessage={ displaySuccessMessage }
                                                                updateLocale={ updateLocale }
                                                                queryLocale={ queryLocale }
                                                                restoring={ restoring }
                                                            /> );
                                                    } } />
                                                </React.Suspense>
                                            </ErrorBoundary>
                                        </div>

                                }
                            </DeckContext.Consumer>
                        }
                    </AppRoot.default>
                </React.Suspense>
            </Bridge>
        </Provider>
    );
}

// need to wrap it in a functional component because useSelector can't be used directly inside the render of the route component
const RouteWrapper = ( props ) => {
    const { route, value, isSignedIn, auth, profileLocale, routes, user, source, displaySuccessMessage, updateLocale, queryLocale, restoring } = props;
    if ( !route || route && route.id === 0 ) {
        return null;
    }
    if ( route && !route.match ) {
        if ( !isSignedIn ) {
            auth.signIn();
            return null;
        } else {
            return (
                <Layout.Content >
                    <Content locale={ profileLocale ? profileLocale : queryLocale || value?.piersUser?.profile?.locale || 'en-US' } route={ '404' } key={ route.pathname } solutionName={ value.piersUser?.solutions[ 0 ]?.solutionName }>
                        <SessionModal auth={ auth } />
                        <NotFound />
                    </Content>

                </Layout.Content>
            );
        }
    }

    // check if the route requires authentication
    // call signIn if it does and there isn't a user
    const requiresAuth = ( route.match.requiresAuth !== false );
    if ( requiresAuth && !isSignedIn ) {
        auth.signIn();
        return null;
    }

    const innerProps = {
        ...route.component.props,
        locale: 'en-US',
        branding: value?.branding?.formattedStyles,
        routes,
        fullRoute: route,
        user,
        source,
        sendErrorEvent,
        company: value.piersUser?.companies.find( company => company.id === value.piersUser?.profile.companyId ),
        solution: value.piersUser?.solutions[ 0 ],
        balance: value.piersUser?.cashier?.points,
        marketplaceItems: value.piersUser?.marketplace?.menuItems,
        solutionId: value.piersUser?.solutions[ 0 ]?.solutionId,
        displaySuccessMessage: displaySuccessMessage,
        isUmbrella: true,
        updateLocale: updateLocale,
        marketplaceLoading: useSelector( ( state ) => state?.marketplaceApp?.loading ),
        redirectUrl: useSelector( ( state ) => state?.marketplaceApp?.redirectUrl ),
        marketplaceError: useSelector( ( state ) => state?.marketplaceApp?.error ) ? true : false,
        feedLoading: useSelector( ( state ) => state?.appTower?.feedLoading ),
        loading: useSelector( ( state ) => state?.appTower?.loading ),
        celebrationLoading: useSelector( ( state ) => state?.appTower?.celebrationLoading ),
        auth: props.auth
    };
    //TODO: guest locale will eventually be default company locale from deck context, but hard-coding for now.
    const locale = requiresAuth && isSignedIn && queryLocale ? queryLocale : requiresAuth && isSignedIn && ( value.piersUser && value.piersUser.profile && value.piersUser.profile.locale !== null ) ? value.piersUser.profile.locale : 'en-US';

    setLocaleInDB( profileLocale ? profileLocale : queryLocale || locale || 'en-US' );

    if ( locale && value.piersUser ) {
        return (
            <Layout.Content className={ route.match?.contentClassName ? route.match.contentClassName : '' } >
                <Content locale={ profileLocale ? profileLocale : queryLocale || locale || 'en-US' } route={ route } key={ route.pathname } solutionName={ value.piersUser?.solutions[ 0 ]?.solutionName }>
                    <SessionModal auth={ auth } />
                    { /* use component directly, calling react.cloneElement recreates the comp on every rerender when called inside a functional comp or in the render of class comp */ }
                    {
                        !restoring ?
                            <route.component.type { ...innerProps } locale={ profileLocale || queryLocale || locale || 'en-US' } />
                            : <Spin spinning={ true } className="restoring-spinner" />
                    }
                </Content>
            </Layout.Content>
        );
    } else {
        return null;
    }
};

const setLocaleInDB = ( locale ) => {
    //open/create database for locale storage
    let db;
    const dbRequest = indexedDB.open( 'LocaleStore' );
    dbRequest.onerror = ( event ) => {
        console.log( 'error opening indexedDB', event );
    };
    dbRequest.onsuccess = ( event ) => {
        //get read/write transaction to update data if data exists once the db has successfully been opened
        db = event.target.result;
        const transaction = db.transaction( [ 'locale' ], 'readwrite' );
        if ( transaction ) {

            const localeObjectStore = transaction.objectStore( 'locale' );
            if ( localeObjectStore ) {
                //get the locale from the db
                const localeStore = localeObjectStore.get( 1 );
                localeStore.onsuccess = ( event ) => {
                    //if the result has a locale, update with new locale
                    if ( localeStore.result && localeStore.result.locale !== locale ) {
                        localeObjectStore.put( { id: 1, locale: locale } );
                    }
                };
                localeStore.onerror = ( event ) => {
                    //log event and do nothing on error
                    console.log( 'error retrieving from db', event );
                };
            }
        }
    };
    dbRequest.onupgradeneeded = ( event ) => {
        const db = event.target.result;
        //create object store for locale - this only fires on creation/upgrade of db
        const objectStore = db.createObjectStore( 'locale', { keyPath: 'id' } );
        //create an index on the id, even though there will only be 1 of them in the locale object
        objectStore.createIndex( 'id', 'id', { unique: true } );

        objectStore.transaction.oncomplete = ( event ) => {
            // Store values in the newly created objectStore.
            const localeObjectStore = db
                .transaction( 'locale', 'readwrite' )
                .objectStore( 'locale' );
            //add locale to object store
            localeObjectStore.add( { id: 1, locale: locale } );


        };
    };
};